import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Icon from './Icon';
import { middleTruncate, withoutLegalSuffix } from '../utils/middleTruncate';

// Active-tenant indicator in the navbar. Always visible so users never lose
// track of which restaurant they're operating on. Admins get a clickable
// chip that opens an inline picker (mirrors UserMenu's TenantPicker, but
// anchored to the navbar). Viewers get a static label.
// The chip shows at most CHIP_MAX characters of a name, shortened from the
// middle; if even that does not fit the chip's width, it steps down a
// character at a time (never below CHIP_MIN) until it does — so the screen
// never cuts the end off a second time.
const CHIP_MAX = 24;
const CHIP_MIN = 8;

export default function TenantChip() {
  const { user, restaurantId, switchRestaurant, restaurants, isRealAdmin } = useAuth();
  // Tenancy follows the REAL role: a read-only preview never changes which
  // restaurants this user can switch between.
  const isAdmin = isRealAdmin;
  // A viewer assigned to multiple restaurants gets the same interactive
  // picker an admin gets — scoped to `restaurants`, which AuthContext
  // populates from `assigned_restaurants` for viewers.
  const canSwitch = isAdmin || restaurants.length >= 2;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  const currentName = useMemo(() => {
    const found = restaurants.find(r => String(r.id) === String(restaurantId));
    if (found) return found.name;
    // Fall back to the user's landing restaurant (covers single-restaurant
    // viewers and admins whose tenant list hasn't loaded yet).
    return user?.restaurant?.name || null;
  }, [restaurants, restaurantId, user]);
  // A long name is shortened from the MIDDLE so its start and its end (often
  // the restaurant itself) both stay visible; the full name stays in the
  // tooltip, the accessible name and the picker list (owner, 2026-10-05).
  const nameRef = useRef(null);
  const [cap, setCap] = useState(CHIP_MAX);
  // Bumped on a WIDTH change: a re-fit must run even when the cap is already
  // CHIP_MAX (setting the same cap would not re-render).
  const [fitPass, setFitPass] = useState(0);
  const resetting = useRef(false);
  const shortName = middleTruncate(withoutLegalSuffix(currentName), cap);
  // A new name, a resize, or the chip turning from a label into a button (the
  // restaurant list loaded: the caret takes room) starts again from the full cap…
  useLayoutEffect(() => { resetting.current = true; setCap(CHIP_MAX); }, [currentName, fitPass, canSwitch]);
  useEffect(() => {
    // Phones fire `resize` as the address bar slides away while scrolling;
    // only a real width change needs a re-fit, once per frame.
    let width = window.innerWidth;
    let frame = 0;
    const onResize = () => {
      if (window.innerWidth === width) return;
      width = window.innerWidth;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setFitPass(n => n + 1));
    };
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); cancelAnimationFrame(frame); };
  }, []);
  // …and steps down until the shortened name fits the chip. Right after a
  // reset to a lower cap, wait for the re-render at CHIP_MAX before measuring
  // (measuring the old text would knock one character off a name that fits).
  useLayoutEffect(() => {
    if (resetting.current) {
      resetting.current = false;
      if (cap !== CHIP_MAX) return;
    }
    const el = nameRef.current;
    if (el && el.scrollWidth > el.clientWidth + 1 && cap > CHIP_MIN) setCap(c => c - 1);
  }, [cap, shortName, fitPass, canSwitch]);

  const filtered = useMemo(() => {
    const q = query.trim();
    // Admins have the full restaurant catalog so a search box pays off;
    // viewers have at most a handful, no need to require 3-char queries.
    if (isAdmin && q.length < 3) return restaurants;
    if (q.length === 0) return restaurants;
    return restaurants.filter(r => r.name && r.name.includes(q));
  }, [restaurants, query, isAdmin]);

  useEffect(() => {
    if (!open) { setQuery(''); return; }
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 0);
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;
  // Admin with id but list still loading: show a skeleton chip so the UI
  // doesn't pop in late. Viewer with no restaurant name: hide entirely.
  if (!currentName) {
    if (isAdmin && restaurantId) {
      return (
        <span className="tenant-chip tenant-chip--viewer" aria-hidden="true">
          <span className="tenant-chip-name">···</span>
        </span>
      );
    }
    return null;
  }

  if (!canSwitch) {
    return (
      <span className="tenant-chip tenant-chip--viewer" title={currentName}>
        <Icon name="restaurant" size={15} />
        {/* <bdi>: a name that starts in Latin keeps its own direction, so its
            "…" lands on the correct side. Screen readers get the full name. */}
        <bdi ref={nameRef} className="tenant-chip-name" aria-hidden="true">{shortName}</bdi>
        <span className="sr-only">{currentName}</span>
      </span>
    );
  }

  return (
    <div className="tenant-chip-wrapper">
      <button
        type="button"
        className={`tenant-chip ${isAdmin ? 'tenant-chip--admin' : 'tenant-chip--viewer'}${open ? ' open' : ''}`}
        title={currentName}
        aria-expanded={open}
        // Starts with the name the chip shows (WCAG 2.5.3), in full.
        aria-label={`${currentName} — מסעדה נוכחית. החלף מסעדה`}
        onClick={() => setOpen(o => !o)}
      >
        <Icon name="restaurant" size={15} />
        <bdi ref={nameRef} className="tenant-chip-name">{shortName}</bdi>
        <span className="tenant-chip-caret" aria-hidden="true"><Icon name="chevron" size={13} strokeWidth={2} /></span>
      </button>

      {open && (
        <>
          <div className="menu-backdrop" onClick={() => setOpen(false)} />
          <div className="tenant-picker-panel tenant-chip-panel">
            {isAdmin && (
              <input
                ref={inputRef}
                className="tenant-picker-input"
                placeholder="חפש מסעדה (3+ תווים)"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            )}
            <div className="tenant-picker-list">
              {filtered.length === 0 ? (
                <div className="tenant-picker-empty">לא נמצאו תוצאות</div>
              ) : (
                filtered.map(r => {
                  const isActive = String(restaurantId) === String(r.id);
                  return (
                    <button
                      key={r.id}
                      type="button"
                      className={`tenant-picker-option${isActive ? ' active' : ''}`}
                      onClick={() => {
                        switchRestaurant(String(r.id));
                        setOpen(false);
                      }}
                    >
                      <span className="tenant-picker-check">{isActive && <Icon name="check" size={13} strokeWidth={2} />}</span>
                      <bdi>{r.name}</bdi>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
